import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/styles";
import {
  TextField,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from "@material-ui/core";
import { useQuery } from "react-query";
import { getCategories } from "../data/services/API";
import { useWorkflowNames } from "../data/workflow";
import { Dropdown } from ".";

const MATCH_TYPES = {
  EXACT: "exact",
  CONTAINS: "contains",
  STARTS_WITH: "startsWith",
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "20px",
    minWidth: "350px",
    borderRadius: "8px",
    backgroundColor: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e0e0e0",
  },
  header: {
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "1px solid #f0f0f0",
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: "14px",
    color: "#333",
  },
  inputWrapper: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "16px",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  labelBox: {
    display: "flex",
    alignItems: "baseline",
    marginBottom: "6px",
    gap: "6px",
  },
  inputLabel: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#4a4a4a",
  },
  helperText: {
    fontSize: "11px",
    color: "#888",
    fontStyle: "italic",
  },
  disabledText: {
    color: "#ccc",
  },
}));

const WorkflowDefinitionSearch = ({ value, onChange }) => {
  const classes = useStyles();

  // State
  const [matchType, setMatchType] = useState(MATCH_TYPES.EXACT);
  const [selectedWorkflow, setSelectedWorkflow] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [textPattern, setTextPattern] = useState("");

  // Queries
  const { data: workflowNames, isFetching } = useWorkflowNames();
  const { data: categoriesData } = useQuery("categories", getCategories);

  const workflowOptions = useMemo(() => {
    return Array.isArray(workflowNames) ? workflowNames : [];
  }, [workflowNames]);

  const categoryOptions = useMemo(() => {
    return Array.isArray(categoriesData?.data) ? categoriesData.data : [];
  }, [categoriesData]);

  // --- FIXED USE EFFECT ---
  useEffect(() => {
    if (value) {
      if (value.isRegex) {
        const val = value.workflowName || "";

        // Case 1: Explicit "Contains" pattern (*text*)
        if (val.length >= 2 && val.startsWith("*") && val.endsWith("*")) {
          setMatchType(MATCH_TYPES.CONTAINS);
          setTextPattern(val.slice(1, -1));
        }
        // Case 2: Explicit "Starts With" pattern (text*)
        else if (val.length >= 1 && val.endsWith("*")) {
          setMatchType(MATCH_TYPES.STARTS_WITH);
          setTextPattern(val.slice(0, -1));
        }
        // Case 3: Empty or Plain text
        else {
          setTextPattern(val);

          // CRITICAL FIX:
          // If the value is empty, we do NOT force a switch to CONTAINS.
          // We only default to CONTAINS if we are currently in EXACT mode
          // (meaning we just loaded a regex filter for the first time).
          // This preserves the user's selection of 'STARTS_WITH' when the box is empty.
          setMatchType((prevType) => {
            if (prevType === MATCH_TYPES.EXACT) return MATCH_TYPES.CONTAINS;
            return prevType;
          });
        }

        // Cleanup Exact match state just in case
        setSelectedWorkflow("");
        setSelectedCategory("");
      } else {
        // Exact Match Logic
        setMatchType(MATCH_TYPES.EXACT);
        setSelectedWorkflow(value.workflowName || "");
        setSelectedCategory(value.category || "");
        setTextPattern("");
      }
    } else {
      // Default / Clear All
      setMatchType(MATCH_TYPES.EXACT);
      setSelectedWorkflow("");
      setSelectedCategory("");
      setTextPattern("");
    }
  }, [value]);

  // --- Handlers ---

  const handleMatchTypeChange = (event) => {
    const newType = event.target.value;
    setMatchType(newType);

    // Reset values when switching types
    if (newType === MATCH_TYPES.EXACT) {
      setTextPattern("");
      triggerChange("", "", false);
    } else {
      setSelectedWorkflow("");
      setSelectedCategory("");
      // When switching to regex modes, send empty string with isRegex=true
      // The useEffect fix above ensures this doesn't revert the dropdown
      triggerChange("", "", true);
    }
  };

  const handleWorkflowChange = (event, newValue) => {
    const val = newValue || "";
    setSelectedWorkflow(val);
    setSelectedCategory("");
    triggerChange(val, "", false);
  };

  const handleCategoryChange = (event, newValue) => {
    const val = newValue || "";
    setSelectedCategory(val);
    triggerChange(selectedWorkflow, val, false);
  };

  const handleTextChange = (event) => {
    const val = event.target.value;
    setTextPattern(val);

    // Construct the backend pattern based on current mode
    let regexPattern = "";
    if (val) {
      if (matchType === MATCH_TYPES.CONTAINS) regexPattern = `*${val}*`;
      else if (matchType === MATCH_TYPES.STARTS_WITH) regexPattern = `${val}*`;
      else regexPattern = val; // Fallback, though shouldn't happen in regex mode
    }

    // Always send isRegex=true for these modes
    triggerChange(regexPattern, "", true);
  };

  const triggerChange = (wfName, cat, isRegex) => {
    if (onChange) {
      onChange({
        workflowName: wfName,
        category: cat,
        isRegex: isRegex,
      });
    }
  };

  return (
    <Paper className={classes.root} elevation={0}>
      {/* 1. Search Method Selector */}
      <div className={classes.inputWrapper}>
        <div className={classes.labelBox}>
          <Typography className={classes.inputLabel}>Search Method</Typography>
        </div>
        <FormControl fullWidth variant="outlined" size="small">
          <Select value={matchType} onChange={handleMatchTypeChange}>
            <MenuItem value={MATCH_TYPES.EXACT}>Exact Match</MenuItem>
            <MenuItem value={MATCH_TYPES.CONTAINS}>Contains</MenuItem>
            <MenuItem value={MATCH_TYPES.STARTS_WITH}>Starts With</MenuItem>
          </Select>
        </FormControl>
      </div>

      {/* 2. Dynamic Input Area based on Match Type */}
      {matchType === MATCH_TYPES.EXACT ? (
        <>
          <div className={classes.inputWrapper}>
            <div className={classes.labelBox}>
              <Typography className={classes.inputLabel}>
                Workflow Name
              </Typography>
            </div>
            <Dropdown
              options={workflowOptions}
              value={selectedWorkflow}
              onChange={handleWorkflowChange}
              placeholder="Select a Workflow..."
              multiple={false}
              freeSolo={false}
              loading={isFetching || workflowOptions.length === 0}
              fullWidth
            />
          </div>

          <div className={classes.inputWrapper}>
            <div className={classes.labelBox}>
              <Typography
                className={`${classes.inputLabel} ${
                  !selectedWorkflow ? classes.disabledText : ""
                }`}
              >
                Category
              </Typography>
              <Typography
                className={`${classes.helperText} ${
                  !selectedWorkflow ? classes.disabledText : ""
                }`}
              >
                (Optional)
              </Typography>
            </div>
            <Dropdown
              options={categoryOptions}
              value={selectedCategory}
              onChange={handleCategoryChange}
              placeholder={
                !selectedWorkflow ? "Select a workflow first" : "Select Type..."
              }
              multiple={false}
              freeSolo={false}
              disabled={!selectedWorkflow}
              fullWidth
            />
          </div>
        </>
      ) : (
        <>
          <div className={classes.inputWrapper}>
            <div className={classes.labelBox}>
              <Typography className={classes.inputLabel}>
                Workflow Pattern
              </Typography>
              <Typography className={classes.helperText}>
                {matchType === MATCH_TYPES.CONTAINS
                  ? "Search for text appearing anywhere"
                  : "Search for text at the beginning"}
              </Typography>
            </div>
            <TextField
              value={textPattern}
              onChange={handleTextChange}
              placeholder="Enter text..."
              variant="outlined"
              size="small"
              fullWidth
              autoFocus
            />
          </div>
        </>
      )}
    </Paper>
  );
};

export default WorkflowDefinitionSearch;
